import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Button,
  Typography,
  Box,
  CardMedia,
  Tabs,
  Tab,
  IconButton,
  Drawer,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Chip,
  Skeleton,
} from "@mui/material";
import { useSnackbar } from "../../../components/utils/SnackBar";
import CloseIcon from "@mui/icons-material/Close";
import color from "../../../components/utils/Colors";
import {
  ChevronLeft,
  Description,
  ExpandMore,
  VideoLibrary,
  Person,
  Language,
  School,
} from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  publishCourse,
  getOneDataFromTable,
  getDataFromTable,
} from "../../../services/services";

interface CourseData {
  id: number;
  title: string;
  subTitle: string;
  description: string;
  level: string;
  language: string;
  timeline: string;
  teachersName: string;
  whoCanJoin: string;
  learningObjectives: string[];
  price: number;
  status: string;
  coverImage?: string;
}

interface ContentItem {
  id: number;
  lectureName: string;
  lectureDesc: string;
  lectureOrder: number;
  contentType: string;
}

const CreateCoursePreview: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const showSnackbar = useSnackbar();
  const [open, setOpen] = useState(false);

  const [course, setCourse] = useState<CourseData | null>(null);
  const [contents, setContents] = useState<ContentItem[]>([]);
  const [loadingCourse, setLoadingCourse] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ courseId?: string }>();

  // Support both URL param (/course-preview/:courseId) and location.state.courseId
  const courseId = params.courseId ?? (location.state as any)?.courseId;

  useEffect(() => {
    if (!courseId) return;

    setLoadingCourse(true);
    Promise.allSettled([
      getOneDataFromTable("course", courseId),
      getDataFromTable("courseContent", { courseId }),
    ])
      .then(([courseRes, contentRes]) => {
        if (courseRes.status === "fulfilled") {
          const raw = (courseRes.value as any)?.data?.data ?? {};
          setCourse({
            id: raw.id,
            title: raw.title ?? raw.courseName ?? "",
            subTitle: raw.subTitle ?? "",
            description: raw.description ?? "",
            level: raw.level ?? "",
            language: raw.language ?? "",
            timeline: raw.timeline ?? raw.duration ?? "",
            teachersName: raw.teachersName ?? "",
            whoCanJoin: raw.whoCanJoin ?? "",
            learningObjectives: Array.isArray(raw.learningObjectives)
              ? raw.learningObjectives
              : [],
            price: raw.price ?? 0,
            status: raw.status ?? "",
            coverImage: process.env.REACT_APP_PUBLIC_BASE_URL+raw.coverImage ?? raw.image ?? "",
          });
        }
        if (contentRes.status === "fulfilled") {
          const raw: any[] = (contentRes.value as any)?.data?.data ?? [];
          setContents(
            raw.map((item: any) => ({
              id: item.id,
              lectureName: item.lectureName ?? "",
              lectureDesc: item.lectureDesc ?? "",
              lectureOrder: item.lectureOrder ?? 0,
              contentType: item.contentType ?? "video",
            })),
          );
        }
      })
      .finally(() => setLoadingCourse(false));
  }, [courseId]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await publishCourse(String(courseId));
      alert("Course published successfully!");
      navigate("/", { state: { courseId } });
    } catch (err: any) {
      showSnackbar(
        err.response?.data?.message ||
          "An error occurred while publishing the course",
      );
    } finally {
      setPublishing(false);
    }
  };

  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <Grid
      container
      item
      xs={12}
      sx={{
        borderBottom: "1px solid",
        borderColor: color.borderColor,
        paddingBottom: 2,
        paddingTop: 2,
        px: 2,
      }}
    >
      <Grid item xs={12} sm={4}>
        <Typography
          variant="h6"
          sx={{ color: color.textColor, fontWeight: "600", fontSize: "16px" }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography
          variant="body1"
          sx={{ color: "rgba(41, 8, 84, 1)", fontSize: "15px" }}
        >
          {value}
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ pb: 4 }}>
      {/* Floating sidebar toggle */}
      <Box sx={{ position: "fixed", top: "125px", right: "0", zIndex: 1000 }}>
        <ChevronLeft
          onClick={() => setOpen(true)}
          sx={{
            border: "solid 1.5px",
            borderColor: color.borderColor,
            cursor: "pointer",
            color: color.firstColor,
            borderRadius: "40px",
            background: color.secondColor,
            boxShadow: 1,
            width: "auto",
            height: { xs: "60px", sm: "60px", md: "60px" },
          }}
        />
      </Box>

      {/* Course Content Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "90%",
            maxWidth: "400px",
            height: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 1,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              top: 20,
              right: 10,
              color: color.firstColor,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{ mt: 0, p: 4, pt: 3, pb: 3, background: color.secondColor }}
          >
            <Typography variant="h6" component="h2">
              Course Content
            </Typography>
          </Box>

          {loadingCourse ? (
            <Box sx={{ p: 2 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={60}
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
          ) : contents.length > 0 ? (
            contents.map((item, idx) => (
              <Accordion
                key={item.id}
                disableGutters
                sx={{
                  boxShadow: 0,
                  m: 1,
                  borderRadius: "4px",
                  border: "solid 1.5px",
                  borderColor: color.firstColor,
                  "&.Mui-expanded": { m: 1 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: color.firstColor }} />}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        flexGrow: 1,
                        color: color.textColor,
                        fontSize: "15px",
                      }}
                    >
                      {item.lectureName || `Section ${idx + 1}`}
                    </Typography>
                    {item.contentType && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: color.textColor,
                          fontWeight: "bold",
                          pr: 2,
                        }}
                      >
                        {item.contentType}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                {item.lectureDesc && (
                  <AccordionDetails>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        padding: "8px",
                        border: "1.5px solid",
                        borderColor: color.borderColor,
                        borderRadius: "4px",
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          sx={{
                            color: color.textColor,
                            fontWeight: 600,
                            fontSize: "14px",
                          }}
                        >
                          {item.lectureDesc}
                        </Typography>
                        <Box display="flex" gap={1}>
                          <IconButton sx={{ color: color.firstColor }}>
                            <VideoLibrary />
                          </IconButton>
                          <IconButton sx={{ color: color.firstColor }}>
                            <Description />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </AccordionDetails>
                )}
              </Accordion>
            ))
          ) : (
            <Typography sx={{ p: 3, color: "gray" }}>
              No content added yet.
            </Typography>
          )}
        </Box>
      </Drawer>

      {/* Top nav bar */}
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          borderRadius: "40px",
          margin: 4,
          mb: 2,
          mt: 2,
          flexGrow: 1,
          width: "auto",
          border: "solid 1.5px",
          borderColor: color.borderColor,
          background: "rgba(241, 235, 249, 1)",
          px: { xs: 2, sm: 4, md: 6 },
          height: "60px",
        }}
      >
        <Typography
          onClick={() => navigate(-1)}
          sx={{
            margin: 0,
            color: color.firstColor,
            fontSize: "16px",
            fontWeight: "bold",
            paddingLeft: "2px",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <ChevronLeft /> Back
        </Typography>
        <Typography
          sx={{
            textAlign: "center",
            flexGrow: 1,
            color: color.textColor,
            fontSize: "20px",
            fontWeight: "bold",
          }}
        >
          {loadingCourse ? (
            <Skeleton width={200} sx={{ mx: "auto" }} />
          ) : (
            course?.title || "Course Preview"
          )}
        </Typography>
      </Grid>

      <Container
        maxWidth="xl"
        sx={{
          minHeight: "100vh",
          backgroundColor: "transparent",
          px: { xs: 2, sm: 4 },
        }}
      >
        {/* Cover image / video placeholder */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 4,
            height: { xs: "300px", sm: "400px", md: "500px" },
            background: "#808080ba",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <CardMedia
            component="img"
            image={course?.coverImage || "/assets/Video-img.png"}
            alt="Course Cover"
            sx={{
              backgroundColor: "#D9D9D9",
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>

        {/* Course title + chips */}
        {loadingCourse ? (
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="40%" height={30} />
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{ color: color.textColor, fontWeight: "bold", mb: 1 }}
            >
              {course?.title}
            </Typography>
            {course?.subTitle && (
              <Typography variant="subtitle1" sx={{ color: "#666", mb: 2 }}>
                {course.subTitle}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {course?.level && (
                <Chip
                  label={course.level}
                  sx={{ background: color.firstColor, color: "white" }}
                />
              )}
              {course?.timeline && (
                <Chip
                  label={course.timeline}
                  variant="outlined"
                  sx={{ borderColor: color.firstColor }}
                />
              )}
              {course?.language && (
                <Chip
                  label={course.language}
                  variant="outlined"
                  sx={{ borderColor: color.firstColor }}
                />
              )}
              {course?.price != null && course.price > 0 && (
                <Chip
                  label={`${course.price}`}
                  sx={{ background: color.thirdColor, color: "white" }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Tabs */}
        <Box
          sx={{
            border: "2px solid",
            borderRadius: "40px",
            overflow: "hidden",
            borderColor: color.borderColor,
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, v) => setSelectedTab(v)}
            centered
            sx={{
              background: color.secondColor,
              ".MuiTab-root": {
                textTransform: "none",
                fontWeight: "bold",
                color: "gray",
                "&.Mui-selected": { color: "rgba(46, 10, 87, 1)" },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "rgba(46, 10, 87, 1)",
                height: "2px",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Notes" />
            <Tab
              label="Question Papers"
              onClick={() => navigate("/question-form-preview")}
            />
            <Tab label="Review" />
          </Tabs>
        </Box>

        {/* About section */}
        <Box sx={{ padding: "30px 0px" }}>
          <Typography
            sx={{
              color: "rgba(46, 10, 87, 1)",
              fontWeight: "600",
              fontSize: { xs: "18px", sm: "20px", md: "24px" },
              textDecoration: "underline",
              mb: 2,
            }}
          >
            About this course
          </Typography>
        </Box>

        {/* Course details grid */}
        <Box sx={{ padding: { xs: 2, sm: 2 } }}>
          <Grid
            container
            sx={{
              border: "2px solid",
              borderRadius: "12px",
              borderColor: color.borderColor,
            }}
          >
            {loadingCourse ? (
              [1, 2, 3, 4].map((i) => (
                <Grid key={i} item xs={12} sx={{ p: 2 }}>
                  <Skeleton variant="text" width="80%" height={30} />
                </Grid>
              ))
            ) : (
              <>
                {course?.teachersName && (
                  <InfoRow
                    label="Instructor"
                    value={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person sx={{ color: color.firstColor }} />
                        {course.teachersName}
                      </Box>
                    }
                  />
                )}
                {course?.level && (
                  <InfoRow
                    label="Course Level"
                    value={
                      <Box display="flex" alignItems="center" gap={1}>
                        <School sx={{ color: color.firstColor }} />
                        {course.level}
                      </Box>
                    }
                  />
                )}
                {course?.language && (
                  <InfoRow
                    label="Language"
                    value={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Language sx={{ color: color.firstColor }} />
                        {course.language}
                      </Box>
                    }
                  />
                )}
                {course?.timeline && (
                  <InfoRow label="Duration" value={course.timeline} />
                )}
                {course?.description && (
                  <InfoRow label="Description" value={course.description} />
                )}
                {course?.whoCanJoin && (
                  <InfoRow label="Who can join" value={course.whoCanJoin} />
                )}
                {course?.learningObjectives &&
                  course.learningObjectives.length > 0 && (
                    <InfoRow
                      label="What you'll learn"
                      value={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {course.learningObjectives.map((obj, i) => (
                            <li key={i}>{obj}</li>
                          ))}
                        </ul>
                      }
                    />
                  )}
                {course?.price != null && (
                  <InfoRow label="Price" value={`${course.price}`} />
                )}
                <InfoRow
                  label="Course available for"
                  value="Android • iOS • Web"
                />
              </>
            )}
          </Grid>
        </Box>

        <Box
          sx={{
            padding: "30px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            id="button"
            variant="outlined"
            sx={{
              borderColor: color.firstColor,
              color: color.firstColor,
              textTransform: "none",
            }}
            onClick={() => navigate("/course-desc", { state: { courseId } })}
          >
            Edit Course
          </Button>
          <Button
            id="publish-button"
            disabled={publishing}
            onClick={handlePublish}
            sx={{
              background: color.firstColor,
              color: "white",
              textTransform: "none",
              "&:hover": { background: color.forthColor },
            }}
          >
            {publishing ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} sx={{ color: "white" }} />
                Publishing...
              </Box>
            ) : (
              "Publish Course"
            )}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CreateCoursePreview;
